<template>
    <form @submit.stop.prevent="handleSubmit" novalidate>
        <div class="row mb-3">
            <div class="col-md-6 col-sm-12">
                <div class="form-group">
                    <label class="d-block">{{ edit == true ? 'Replace floor plan' : 'Upload floor plan' }} <small class="text-muted">(optional)</small></label>
                    <photo-source-picker
                        accept="image/*,application/pdf"
                        gallery-label="Choose file"
                        desktop-label="Choose file"
                        :disabled="preparingFile"
                        @selected="onFloorplanSelected"
                    />
                    <div v-if="preparingFile" class="d-flex align-items-center text-muted small mt-2">
                        <span class="spinner-border spinner-border-sm mr-2" role="status"></span>Preparing photo…
                    </div>
                    <div v-else-if="filename" class="d-flex align-items-center small mt-2">
                        <button
                            v-if="floorplanPreview"
                            type="button"
                            class="floorplan-thumb-btn mr-2 flex-shrink-0"
                            aria-label="View new floor plan"
                            @click="$refs.lightbox.open(floorplanPreview, { title: 'New floor plan' })"
                        >
                            <img :src="floorplanPreview" class="floorplan-thumb rounded" alt="New floor plan">
                        </button>
                        <i v-else class="las la-file-pdf la-lg text-danger mr-1"></i>
                        <span class="text-truncate">{{ filename }}</span>
                    </div>
                    <a v-if="edit == true && form.floorplan != null" :href="form.floorplan" target="_blank" class="mt-1 d-inline-flex align-items-center small text-primary floorplan-link" @click="viewCurrentFloorplan">
                        <i class="las la-file-alt mr-1"></i>View Current Floor Plan
                    </a>
                    <image-lightbox ref="lightbox" />
                </div>
            </div>

            <div class="col-md-6 col-sm-12">
                <div class="form-group">
                    <label for="house-status">Status *</label>
                    <select id="house-status" v-model="form.house_status_id" class="form-control" :class="{ 'is-invalid' : $v.form.house_status_id.$error }">
                        <option :value="null">Please select</option>
                        <option value="1">Owner and Occupant</option>
                        <option value="2">Owner and not Occupant</option>
                        <option value="3">Other</option>
                    </select>
                    <div class="invalid-feedback">Please choose whether you live in this house</div>
                </div>
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-md-6 col-sm-12">
                <div class="form-group">
                    <label for="house-label">Name of house *</label>
                    <input id="house-label" v-model="form.label" type="text" class="form-control" placeholder="e.g. My House" :class="{ 'is-invalid' : $v.form.label.$error }">
                    <div class="invalid-feedback">Please give your house a name</div>
                </div>
            </div>
            <div class="col-md-6 col-sm-12">
                <div class="form-group">
                    <label for="house-type">Type of house *</label>
                    <select id="house-type" v-model="houseTypeSelection" class="form-control" :class="{ 'is-invalid' : $v.form.house_type_id.$error && houseTypeSelection !== 'other' }" @change="onHouseTypeChange">
                        <option :value="null">Please select</option>
                        <option v-for="houseType in houseTypes" :key="houseType.id" :value="houseType.id">{{ houseType.name }}</option>
                        <option value="other">Other</option>
                    </select>
                    <input v-if="houseTypeSelection === 'other'" v-model="form.house_type_other" type="text" class="form-control mt-2" maxlength="255" placeholder="Enter type of house" :class="{ 'is-invalid' : $v.form.house_type_other.$error }">
                    <div class="invalid-feedback">{{ houseTypeSelection === 'other' ? 'Please enter the type of house' : 'Please choose the type of house, or Other if it is not listed' }}</div>
                </div>
            </div>
        </div>

        <!-- Address Information Section Header -->
        <div class="row mt-3 mb-2">
            <div class="col-12">
                <div class="d-flex justify-content-between align-items-center border-bottom pb-2">
                    <h6 class="font-weight-bold text-dark mb-0">
                        <i class="las la-map-marker-alt text-primary mr-1"></i> Address Information
                    </h6>
                    <transition name="fade">
                        <span v-if="autoFilledMessage" class="badge badge-success px-2 py-1 font-weight-normal">
                            <i class="las la-magic mr-1"></i> {{ autoFilledMessage }}
                        </span>
                    </transition>
                </div>
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-md-6 col-sm-12">
                <div class="form-group">
                    <label for="house-line-1">Address line 1 *</label>
                    <input id="house-line-1" v-model="form.address.line_1" type="text" autocomplete="address-line1" class="form-control" placeholder="Street name, house/unit number" :class="{ 'is-invalid' : $v.form.address.line_1.$error }">
                    <div class="invalid-feedback">Please enter your address</div>
                </div>
            </div>
            <div class="col-md-6 col-sm-12">
                <div class="form-group">
                    <label for="house-line-2">Address line 2</label>
                    <input id="house-line-2" v-model="form.address.line_2" type="text" autocomplete="address-line2" class="form-control" placeholder="Building, suite, floor (optional)">
                </div>
            </div>
        </div>

        <div class="row mb-3">
            <!-- Postcode Field: first, because it auto-fills State & City -->
            <div class="col-md-4 col-sm-12">
                <div class="form-group">
                    <div class="d-flex justify-content-between align-items-center">
                        <label for="house-postcode">Postcode *</label>
                        <small v-if="isLookupLoading" class="text-primary"><i class="las la-spinner la-spin"></i> Looking up...</small>
                        <small v-else-if="loadingPostcodes" class="text-muted"><i class="las la-spinner la-spin"></i> Loading...</small>
                    </div>
                    <div class="input-group">
                        <input id="house-postcode" v-model="form.address.postcode" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="postal-code" class="form-control" placeholder="e.g. 50000" maxlength="5" :class="{ 'is-invalid' : $v.form.address.postcode.$error }" @input="onPostcodeInput" list="postcode-options">
                        <datalist id="postcode-options">
                            <option v-for="pc in postcodes" :key="pc.id" :value="pc.code">{{ pc.code }}</option>
                        </datalist>
                        <div class="invalid-feedback">Please enter the postcode</div>
                    </div>
                    <small class="form-text text-muted">Enter the 5-digit postcode and we'll fill in the state and city.</small>
                </div>
            </div>

            <!-- State Field -->
            <div class="col-md-4 col-sm-12">
                <div class="form-group">
                    <label for="house-state">State *</label>
                    <select id="house-state" v-model="form.address.state_id" class="form-control" :class="{ 'is-invalid' : $v.form.address.state_id.$error }" @change="onStateChange(true)">
                        <option :value="null">Select State</option>
                        <option v-for="state in states" :key="state.id" :value="state.id">{{ state.name }}</option>
                    </select>
                    <div class="invalid-feedback">Please choose the state</div>
                </div>
            </div>

            <!-- City Field -->
            <div class="col-md-4 col-sm-12">
                <div class="form-group">
                    <div class="d-flex justify-content-between align-items-center">
                        <label for="house-city">City *</label>
                        <small v-if="loadingCities" class="text-muted"><i class="las la-spinner la-spin"></i> Loading...</small>
                    </div>
                    <select v-if="cities && cities.length > 0" id="house-city" v-model="form.address.city_id" class="form-control" :class="{ 'is-invalid' : $v.form.address.city.$error }" @change="onCityChange(true)">
                        <option :value="null">Select City</option>
                        <option v-for="city in cities" :key="city.id" :value="city.id">{{ city.name }}</option>
                    </select>
                    <input v-else id="house-city" v-model="form.address.city" type="text" autocomplete="address-level2" class="form-control" placeholder="e.g. Shah Alam" :class="{ 'is-invalid' : $v.form.address.city.$error }" :disabled="!form.address.state_id && !loadingCities">
                    <div class="invalid-feedback">Please choose the city</div>
                </div>
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-md-6 col-sm-12">
                <div class="form-group">
                    <label for="house-developer">Developer *</label>
                    <select id="house-developer" v-model="developerSelection" class="form-control" :class="{ 'is-invalid' : $v.form.information.developer.$error && developerSelection !== 'other' }" @change="onDeveloperChange">
                        <option :value="null">Please select</option>
                        <option v-for="developer in developers" :key="developer.id" :value="developer.id">{{ developer.name }}</option>
                        <option value="other">Other</option>
                    </select>
                    <input v-if="developerSelection === 'other'" v-model="form.information.developer" type="text" class="form-control mt-2" placeholder="Enter developer name" :class="{ 'is-invalid' : $v.form.information.developer.$error }">
                    <div class="invalid-feedback">{{ developerSelection === 'other' ? 'Please enter the developer name' : 'Please choose the developer, or Other if it is not listed' }}</div>
                </div>
            </div>
            <div class="col-md-6 col-sm-12">
                <div class="form-group">
                    <div class="d-flex justify-content-between align-items-center">
                        <label for="house-local-authority">Local authority *</label>
                        <small v-if="loadingLocalAuthorities" class="text-muted"><i class="las la-spinner la-spin"></i> Loading...</small>
                    </div>
                    <select id="house-local-authority" v-model="form.information.local_authority_id" class="form-control" :class="{ 'is-invalid' : $v.form.information.local_authority.$error }" :disabled="!form.address.state_id" @change="onLocalAuthorityChange">
                        <option :value="null">{{ form.address.state_id ? 'Please select' : 'Enter the postcode or state first' }}</option>
                        <option v-for="la in localAuthorities" :key="la.id" :value="la.id">{{ la.name }}</option>
                    </select>
                    <div class="invalid-feedback">Please choose the local authority (majlis)</div>
                    <small v-if="form.information.local_authority && !form.information.local_authority_id" class="form-text text-muted">
                        Current value: "{{ form.information.local_authority }}"
                    </small>
                </div>
            </div>
        </div>

        <div class="card-footer bg-transparent px-0">
            <div class="d-flex justify-content-end">
                <a :href="route('owner.houses.index')" class="btn btn-admin btn-empty px-4 mr-2" :class="{ disabled: saving }">Cancel</a>
                <button type="submit" class="btn btn-admin btn-primary px-5" :disabled="saving">
                    <template v-if="saving">
                        <span class="spinner-border spinner-border-sm mr-1" role="status"></span>Saving…
                    </template>
                    <template v-else>Save</template>
                </button>
            </div>
        </div>
    </form>
</template>

<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import compressImage from '../../utils/compressImage';
import focusFirstError from '../../utils/focusFirstError';

export default {
    props: ['houseSlug', 'edit'],
    data() {
        return this.initData();
    },
    beforeDestroy() {
        this.setFloorplanPreview(null);
    },
    mounted() {
        this.loadState();
        Promise.all([this.loadHouseType(), this.loadDevelopers()]).then(() => {
            if (this.edit == true) {
                this.loadHouse();
            }
        });
    },
    validations: {
        form: {
            label: { required },
            house_type_id: {
                required: requiredIf(function () {
                    return this.houseTypeSelection !== 'other';
                }),
            },
            house_type_other: {
                required: requiredIf(function () {
                    return this.houseTypeSelection === 'other';
                }),
            },
            house_status_id: { required },
            address: {
                line_1: { required },
                city: { required },
                postcode: { required },
                state_id: { required },
            },
            information: {
                developer: { required },
                local_authority: { required },
            }
        }
    },
    methods: {
        initData() {
            return {
                form: {
                    label: null,
                    house_type_id: null,
                    house_type_other: null,
                    house_status_id: null,
                    address: {
                        line_1: null,
                        line_2: null,
                        city: null,
                        city_id: null,
                        postcode: null,
                        postcode_id: null,
                        state_id: null,
                    },
                    information: {
                        developer: null,
                        developer_id: null,
                        local_authority: null,
                        local_authority_id: null,
                    },
                    floorplan: null
                },
                file: '',
                filename: '',
                floorplanPreview: null,
                preparingFile: false,
                houseTypes: [],
                houseTypeSelection: null,
                developers: [],
                developerSelection: null,
                states: [],
                cities: [],
                postcodes: [],
                localAuthorities: [],
                loadingCities: false,
                loadingPostcodes: false,
                loadingLocalAuthorities: false,
                isLookupLoading: false,
                autoFilledMessage: '',
                lookupTimeout: null,
                saving: false
            }
        },
        async loadHouse() {
            try {
                const { data } = await axios.get(route("owner.houses.show", this.houseSlug));
                this.form = data.data;
                this.syncHouseTypeSelection();
                this.syncDeveloperSelection();

                if (this.form.address && this.form.address.state_id) {
                    await this.onStateChange(false);
                    if (this.form.address.city_id) {
                        await this.onCityChange(false);
                    }
                }
            } catch (e) {
                console.log(e);
                this.$swal('Error!', '', 'error');
            }
        },
        async loadHouseType() {
            try {
                const { data } = await axios.get(route("owner.houses.types.list"));
                this.houseTypes = data;
            } catch (e) {
                console.log(e);
                this.$swal('Error!', '', 'error');
            }
        },
        async loadDevelopers() {
            try {
                const { data } = await axios.get(route("owner.houses.developers.list"));
                this.developers = data;
            } catch (e) {
                console.log(e);
            }
        },
        syncHouseTypeSelection() {
            const houseTypeId = this.form.house_type_id;
            if (houseTypeId && this.houseTypes.some(t => t.id === houseTypeId)) {
                this.houseTypeSelection = houseTypeId;
            } else if (this.form.house_type_other) {
                this.form.house_type_id = null;
                this.houseTypeSelection = 'other';
            } else {
                this.houseTypeSelection = null;
            }
        },
        onHouseTypeChange() {
            if (this.houseTypeSelection === 'other') {
                this.form.house_type_id = null;
                return;
            }

            this.form.house_type_id = this.houseTypeSelection;
            this.form.house_type_other = null;
        },
        syncDeveloperSelection() {
            const developerId = this.form.information.developer_id;
            if (developerId && this.developers.some(d => d.id === developerId)) {
                this.developerSelection = developerId;
            } else if (this.form.information.developer) {
                this.form.information.developer_id = null;
                this.developerSelection = 'other';
            } else {
                this.developerSelection = null;
            }
        },
        onDeveloperChange() {
            if (this.developerSelection === 'other') {
                this.form.information.developer_id = null;
                this.form.information.developer = null;
                return;
            }

            const selected = this.developers.find(d => d.id === this.developerSelection);
            this.form.information.developer_id = selected ? selected.id : null;
            this.form.information.developer = selected ? selected.name : null;
        },
        async loadState() {
            try {
                const { data } = await axios.get(route("locations.states"));
                this.states = data;
            } catch (e) {
                console.log(e);
            }
        },
        async onStateChange(resetChildFields = true) {
            if (resetChildFields) {
                this.form.address.city_id = null;
                this.form.address.city = null;
                this.form.address.postcode_id = null;
                this.form.information.local_authority_id = null;
                this.form.information.local_authority = null;
                this.cities = [];
                this.postcodes = [];
                this.localAuthorities = [];
            }

            if (!this.form.address.state_id) {
                this.cities = [];
                this.localAuthorities = [];
                return;
            }

            this.loadingCities = true;
            try {
                const { data } = await axios.get(route("locations.cities"), {
                    params: { state_id: this.form.address.state_id }
                });
                this.cities = data;
            } catch (e) {
                console.log(e);
            } finally {
                this.loadingCities = false;
            }

            await this.loadLocalAuthorities();
        },
        async loadLocalAuthorities() {
            if (!this.form.address.state_id) {
                this.localAuthorities = [];
                return;
            }

            this.loadingLocalAuthorities = true;
            try {
                const { data } = await axios.get(route("locations.local-authorities"), {
                    params: { state_id: this.form.address.state_id }
                });
                this.localAuthorities = data;
            } catch (e) {
                console.log(e);
            } finally {
                this.loadingLocalAuthorities = false;
            }
        },
        onLocalAuthorityChange() {
            const selected = this.localAuthorities.find(la => la.id === this.form.information.local_authority_id);
            this.form.information.local_authority = selected ? selected.name : null;
        },
        async onCityChange(resetChildFields = true) {
            if (this.form.address.city_id) {
                const selectedCity = this.cities.find(c => c.id === this.form.address.city_id);
                if (selectedCity) {
                    this.form.address.city = selectedCity.name;
                }

                if (!this.form.information.local_authority_id) {
                    const matches = this.localAuthorities.filter(la => la.city_id === this.form.address.city_id);
                    if (matches.length === 1) {
                        this.form.information.local_authority_id = matches[0].id;
                        this.form.information.local_authority = matches[0].name;
                    }
                }
            }

            if (resetChildFields) {
                this.form.address.postcode_id = null;
                this.postcodes = [];
            }

            if (!this.form.address.city_id) {
                this.postcodes = [];
                return;
            }

            this.loadingPostcodes = true;
            try {
                const { data } = await axios.get(route("locations.postcodes"), {
                    params: { city_id: this.form.address.city_id }
                });
                this.postcodes = data;
            } catch (e) {
                console.log(e);
            } finally {
                this.loadingPostcodes = false;
            }
        },
        onPostcodeInput() {
            const rawCode = (this.form.address.postcode || '').replace(/\D/g, '').slice(0, 5);
            this.form.address.postcode = rawCode;

            if (this.lookupTimeout) {
                clearTimeout(this.lookupTimeout);
            }

            if (/^\d{5}$/.test(rawCode)) {
                this.lookupTimeout = setTimeout(() => {
                    this.performPostcodeLookup(rawCode);
                }, 300);
            }
        },
        async performPostcodeLookup(code) {
            this.isLookupLoading = true;
            try {
                const { data } = await axios.get(route("locations.lookup"), {
                    params: { postcode: code }
                });

                if (data && data.found) {
                    this.form.address.state_id = data.state_id;
                    await this.onStateChange(false);

                    this.form.address.city_id = data.city_id;
                    this.form.address.city = data.city_name;
                    await this.onCityChange(false);

                    this.form.address.postcode_id = data.postcode_id;

                    this.autoFilledMessage = `Auto-filled ${data.state_name}, ${data.city_name}`;
                    setTimeout(() => {
                        this.autoFilledMessage = '';
                    }, 4000);
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.isLookupLoading = false;
            }
        },
        async onFloorplanSelected(original) {
            this.preparingFile = true;
            // Keep plans sharper than defect photos so room labels stay readable
            const file = await compressImage(original, { maxDimension: 2400, quality: 0.85 });
            this.preparingFile = false;

            this.file = file;
            this.filename = file.name;
            this.setFloorplanPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
        },
        setFloorplanPreview(url) {
            if (this.floorplanPreview) {
                URL.revokeObjectURL(this.floorplanPreview);
            }
            this.floorplanPreview = url;
        },
        viewCurrentFloorplan(event) {
            if (/\.pdf($|\?)/i.test(this.form.floorplan)) {
                return;
            }
            event.preventDefault();
            this.$refs.lightbox.open(this.form.floorplan, { title: 'Floor plan' });
        },
        handleSubmit() {
            if (this.saving) {
                return;
            }

            this.$v.form.$touch();
            if (this.$v.form.$anyError) {
                this.showFirstError();
                return;
            }

            if (this.edit == true) {
                this.submitUpdate();
            } else {
                this.submitSave();
            }
        },
        showFirstError() {
            this.$nextTick(() => focusFirstError(this.$el, { swal: this.$swal }));
        },
        messageFromError(e) {
            const response = e && e.response;
            if (!response) {
                return 'No internet connection. Please try again.';
            }
            if (response.status === 413) {
                return 'The floor plan file is too large.';
            }
            if (response.status === 422 && response.data && response.data.errors) {
                return Object.values(response.data.errors)[0][0];
            }
            return (response.data && response.data.message) || 'Something went wrong. Please try again.';
        },
        buildFormData() {
            const formData = new FormData();
            formData.append('file', this.file);
            formData.append('form', JSON.stringify(this.form));
            return formData;
        },
        async submitSave() {
            this.saving = true;
            let data;
            try {
                ({ data } = await axios.post(route("owner.houses.store"), this.buildFormData()));
            } catch (e) {
                this.saving = false;
                this.$swal('Not saved', this.messageFromError(e), 'error');
                return;
            }

            const slug = data && data.houseArea ? data.houseArea.slug : null;
            const result = await this.$swal({
                title: 'House added!',
                text: 'Ready to check it for defects? You can do it room by room.',
                icon: 'success',
                showCancelButton: !!slug,
                confirmButtonColor: '#3EC0AD',
                cancelButtonColor: '#3F3D56',
                confirmButtonText: slug ? 'Start Assessment' : 'OK',
                cancelButtonText: 'Later',
                reverseButtons: true,
                allowOutsideClick: false,
            });

            window.location.replace(result.isConfirmed && slug
                ? route('owner.assessments.selectHouse', { house: slug })
                : route('owner.houses.index'));
        },
        async submitUpdate() {
            const formData = this.buildFormData();
            formData.append('_method', 'PATCH');

            this.saving = true;
            try {
                await axios.post(route("owner.houses.update", this.houseSlug), formData);
            } catch (e) {
                this.$swal('Not saved', this.messageFromError(e), 'error');
                return;
            } finally {
                this.saving = false;
            }

            this.$swal({
                toast: true,
                position: 'top',
                icon: 'success',
                title: 'House details saved',
                showConfirmButton: false,
                timer: 2500,
            });
        },
    }
}
</script>

<style scoped>
.floorplan-thumb-btn {
    padding: 0;
    border: 0;
    background: none;
    line-height: 0;
}
.floorplan-thumb {
    width: 56px;
    height: 56px;
    object-fit: cover;
}
.floorplan-link {
    min-height: 44px;
}
.text-indigo {
    color: #3730a3;
}
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.4s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
